import Compressor from 'compressorjs';
import ApiService from '../_shared/api/apiService';
import Singleton from '../Singleton';
import CoreAppService from '../_shared/api/coreAppService';

export default class SettingsService extends Singleton<SettingsService> {
  /**
   * Public Methods
   */
  public getAutoWinSetting = async () => {
    const client = await CoreAppService();

    try {
      const autoWinSetting = await client.settings.getAutoWinSetting();
      return autoWinSetting?.data.autoWinEnabled;
    } catch (e) {
      // TODO handle exception
      return e;
    }
  };

  public updateAutoWinSetting = async autoWinEnabled => {
    const client = await CoreAppService();

    try {
      const update = await client.settings.updateAutoWinSetting({
        autoWinEnabled
      });
      return update;
    } catch (e) {
      // TODO handle exception
      return e;
    }
  };

  public getNotificationSettings = async () => {
    const client = await ApiService();

    try {
      const details = await client.apis.settings.userNotificationSettingsDetails();

      return details?.body;
    } catch (e) {
      // TODO handle exception
      return e;
    }
  };

  public updateNotificationSettings = async (settings, presetId) => {
    const client = await ApiService();
    try {
      const details = await client.apis.settings.userNotificationSettingsDetails();

      // Preserve current settings and append in new settings
      const oldSettings = await details?.body?.userNotificationSettings
        ?.notificationMethods;
      const newSettings = {};

      Object.keys(oldSettings).forEach(setting => {
        newSettings[setting] = {};

        Object.keys(oldSettings[setting]).forEach(type => {
          if (!settings[setting][type]) {
            newSettings[setting][type] = [...oldSettings[setting][type]];
          } else {
            newSettings[setting][type] = [
              ...oldSettings[setting][type],
              ...settings[setting][type]
            ];
          }
        });
      });

      const update = await client.apis.settings.updateUserNotificationSettings({
        request: {
          request: {
            presetId,
            preferenceMethods: [0],
            userNotificationSettings: newSettings
          }
        }
      });

      return update;
    } catch (e) {
      return e;
    }
  };

  public getProSubscriptionDetails = async () => {
    const client = await ApiService();

    try {
      const details = await client.apis.mya.getProSubscriptionDetails();
      return details?.body;
    } catch (e) {
      return e;
    }
  };

  public getCustomizationMetadata = async () => {
    const client = await CoreAppService();

    try {
      const retailSetting = await client.settings.getCustomizationMetadata();
      return retailSetting?.data;
    } catch (e) {
      return e;
    }
  };

  public updateDealerRetailInfo = async requestBody => {
    const client = await CoreAppService();

    try {
      const update = await client.settings
        .updateDealerRetailInfo(requestBody)
        .catch(e => ({
          isError: true,
          errorMessage: e.response?.data?.message,
          errorCode: e.response?.status
        }));
      return update;
    } catch (e) {
      throw new Error(e);
    }
  };

  public getDealerRetailInfo = async () => {
    const client = await CoreAppService();

    try {
      const dealerRetailInfo = await client.settings
        .getDealerRetailInfo()
        .catch(e => ({
          isError: true,
          errorMessage: e.response?.data?.message,
          errorCode: e.response?.status
        }));
      return dealerRetailInfo;
    } catch (e) {
      throw new Error(e);
    }
  };

  public getPresignedUrl = async (fileType: string) => {
    const client = await CoreAppService();

    try {
      const presignedUrl = await client.dealer
        .getPresignedUrls({ fileType })
        .catch(e => ({
          isError: true,
          errorMessage: e.response?.data?.message,
          errorCode: e.response?.status
        }));
      return presignedUrl;
    } catch (e) {
      throw new Error(e);
    }
  };

  static uploadFileToS3UsingPresignedUrl = async (
    presignedUrl: string,
    file: Blob,
    fileType: string
  ) => {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': fileType,
        'x-amz-acl': 'bucket-owner-full-control'
      },
      body: file
    };

    const uploadToS3Response = await fetch(presignedUrl, requestOptions);
    return uploadToS3Response;
  };

  private static compressFile(
    file: Blob | File,
    options: Compressor.Options | undefined
  ) {
    return (() => new Compressor(file, options))();
  }

  static uploadFile(presignedUrl: string, file: Blob, fileType: string) {
    return new Promise((resolve: any, reject: any) =>
      SettingsService.compressFile(file, {
        quality: 0.8,
        convertSize: Infinity,
        success: compressedFile => {
          SettingsService.uploadFileToS3UsingPresignedUrl(
            presignedUrl,
            compressedFile,
            fileType
          )
            .then(() => resolve(presignedUrl))
            .catch(e => {
              reject(e);
            });
        },
        error: reject
      })
    );
  }
}
